'use client';

import { type RefObject, useCallback, useEffect, useRef } from 'react';

interface UseShareImageBlobOptions {
  /** Render options passed straight to modern-screenshot. */
  scale?: number;
  width?: number;
  height?: number;
  /** Warm while the final card is on screen (dialog open, data loaded). */
  warm: boolean;
  /** Re-warms when the card's visuals change without resizing it (e.g. background). */
  warmKey?: string;
  /** Nodes to leave out of the image (modern-screenshot's `filter`). */
  filter?: (node: Node) => boolean;
}

/**
 * Renders a share-card DOM node to a PNG blob. Pre-renders while the card is
 * on screen: generating on click takes seconds, which lets the tap's user
 * activation expire before navigator.share fires (NotAllowedError → fallback
 * panel instead of the sheet). Returns a getter that resolves the warm blob
 * or generates one on demand.
 */
export function useShareImageBlob(
  cardRef: RefObject<HTMLElement | null>,
  { scale, width, height, warm, warmKey, filter }: UseShareImageBlobOptions,
) {
  const generate = useCallback(async () => {
    const node = cardRef.current;
    if (!node) return null;

    const { domToBlob } = await import('modern-screenshot');
    return domToBlob(node, {
      scale: scale ?? 1,
      width,
      height,
      filter,
      fetch: { requestInit: { mode: 'cors' } },
    });
  }, [cardRef, scale, width, height, filter]);

  /** In-flight promise of the pre-rendered blob; null while warming is off. */
  const warmBlobPromise = useRef<Promise<Blob | null> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: warmKey re-warms on card visual changes
  useEffect(() => {
    warmBlobPromise.current = warm ? generate().catch(() => null) : null;
  }, [warm, warmKey, generate]);

  const getBlob = useCallback(async () => {
    const warmBlob = await warmBlobPromise.current;
    return warmBlob ?? generate();
  }, [generate]);

  return getBlob;
}
